import { GraphQL, useRpiGraphqlRequest } from "@rpi/openapi-api";
import { showOldNotification as showNotification, IconV3 } from "@rpi/openapi-core";
import { Button, tokens } from "@blurb/codex-react";
import { createStyles, MantineProvider } from "@mantine/core";
import React from "react";
import { useOutlet } from "react-router-dom";
import { Body, Heading } from "../../../components/Typography";
import { Pagination } from "../../../components/Pagination";
import { RpiTableContainer } from "../../../components/RpiTableContainer";
import { usePageShellStyles } from "../../../components/pageShellStyles";
import { SearchFilterBar } from "../../../components/search-filter/SearchFilterBar";
import { featureFlags } from "../../../featureFlags";
import { getPaymentCsvColumns, paymentColumns } from "../../../configuration/columns/payment.column";
import { SearchInput } from "../../../configuration/filters/searchInput";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { useRpiPagination } from "../../../hooks/usePagination";
import { RpiTable } from "../../../rpi-core/table/RpiTable";
import { rpaTheme } from "../../../theme/rpaTheme";
import { toCsvBlob } from "../../../utils/csv.util";
import { downloadBlob } from "../../../utils/file.util";
import { SearchGeneral, SearchSort } from "../../../utils/graphql.util";
import { paymentSearchLabels, paymentSearchRenderers } from "../../../utils/searchField.util";
import { PaymentInformationSection } from "./PaymentInformationSection";
import { PaymentsFilter } from "./PaymentsFilter";
import { TaxExemptionSection } from "./TaxExemptionSection";

function getPaymentsSubtitle(isSuperAdmin: boolean): string {
  return isSuperAdmin
    ? "This table shows only credit card transactions. Invoice payments will not appear here."
    : "This table shows only transactions made with your credit cards. Invoice payments will not appear here.";
}

function getPaymentsEmptyMessage(isSandbox: boolean): string {
  return isSandbox ? "There are no payments for test orders in the sandbox." : "No payments found.";
}

const useStyles = createStyles({
  header: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
  },
  title: {
    margin: 0,
    color: tokens.color.semantic.text.bold,
  },
  subtitle: {
    margin: 0,
    color: tokens.color.primitive.base.black,
  },
  // Center-align override for the shared row — Billing has no chips slot, so flex-start reads off.
  controlsRowCentered: {
    alignItems: "center",
  },
});

function getSearchPlaceholder(isSuperAdmin: boolean): string {
  return isSuperAdmin ? "Search by Payment ID or Braintree ID" : "Search by Payment ID";
}

export const BillingView: React.FC = () => {
  const user = useAuthUser();
  const outlet = useOutlet();
  const { classes, cx } = useStyles();
  const { classes: shellClasses } = usePageShellStyles();

  const [sortInput, setSortInput] = React.useState<SearchSort<GraphQL.PaymentsQueryVariables>>();
  const [searchInputs, setSearchInputs] = React.useState<SearchInput<GraphQL.PaymentSearchFields>[]>([]);
  const [searchGeneral, setSearchGeneral] = React.useState<SearchGeneral<GraphQL.PaymentsQueryVariables>>();
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const pagination = useRpiPagination({ perPage: 20 });

  const handleSearchPaymentsSubmit = (searchValue: string) => {
    setSearchGeneral(searchValue || undefined);
    pagination.firstPage();
  };

  const search = { inputs: searchInputs, sort: sortInput, generalInput: searchGeneral };

  const { data: paymentsData, ...paymentsQuery } = GraphQL.usePaymentsQuery(
    { limit: pagination.perPage, offset: pagination.offset, search },
    {
      enabled: !user.isSandbox,
      onSuccess: (data) => pagination.setTotalCount(data.payments.totalCount),
    }
  );
  const payments = paymentsData?.payments.data;
  const totalCount = paymentsData?.payments.totalCount ?? 0;

  const fetchAllPayments = useRpiGraphqlRequest<GraphQL.PaymentsQuery, GraphQL.PaymentsQueryVariables>(
    GraphQL.PaymentsDocument
  );

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const result = await fetchAllPayments({ limit: totalCount, offset: 0, search });
      const blob = toCsvBlob(result.payments.data, getPaymentCsvColumns(user.is.SUPER_ADMIN));
      downloadBlob(blob, "payments.csv");
    } catch (error) {
      showNotification({
        title: "Failed to download payments.",
        message: error instanceof Error ? error.message : "Please try again.",
        color: "red",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const tablePayments = user.isSandbox ? [] : payments;
  const tableEmptyMessage = getPaymentsEmptyMessage(user.isSandbox);

  // Payment-method and tax-exemption management moved to Account Settings V2; hide the legacy Payments-page copies once that UI is live.
  const showLegacyAccountManagement = !featureFlags.newAccountSettings;
  const showSavedPaymentMethods =
    showLegacyAccountManagement && !user.isSandbox && (user.is.USER || user.is.CUSTOMER_ADMIN);
  const showTaxExemption = showLegacyAccountManagement && !user.isSandbox && user.is.CUSTOMER_ADMIN;

  return (
    <div className={shellClasses.container} data-testid="billing-view">
      {outlet}

      <div className={classes.header}>
        <Heading as="h1" size="5xl" weight="semibold" className={classes.title}>
          Payments
        </Heading>

        <Body as="p" className={classes.subtitle}>
          {getPaymentsSubtitle(user.is.SUPER_ADMIN)}
        </Body>
      </div>

      <div className={shellClasses.scrollStack}>
        <div className={cx(shellClasses.row, classes.controlsRowCentered)}>
          <div className={shellClasses.searchSlot}>
            <SearchFilterBar
              placeholder={getSearchPlaceholder(user.is.SUPER_ADMIN)}
              isLoading={paymentsQuery.isLoading}
              filters={searchInputs}
              onSetFilters={setSearchInputs}
              onSearchSubmit={handleSearchPaymentsSubmit}
              onFilterClick={() => setIsFilterOpen(true)}
              filterLabels={paymentSearchLabels}
              valueRenderers={paymentSearchRenderers}
              wrapPlaceholder
            />
          </div>
          <Button
            variant="outlined"
            intent="primary"
            size="small"
            loading={isDownloading}
            disabled={isDownloading || totalCount === 0}
            onClick={() => {
              void handleDownload();
            }}
            data-testid="payments-download-button"
          >
            <IconV3 icon="download" size={20} />
            Download
          </Button>
        </div>

        <MantineProvider theme={rpaTheme}>
          <RpiTableContainer
            data-testid="payments-table"
            footer={
              !user.isSandbox && (
                <Pagination isLoading={paymentsQuery.isLoading} pagination={pagination} label="Payments" />
              )
            }
          >
            <RpiTable
              emptyMessage={tableEmptyMessage}
              isLoading={!user.isSandbox && paymentsQuery.isLoading}
              error={paymentsQuery.error}
              perPage={payments?.length}
              data={tablePayments}
              columns={paymentColumns}
              withSortBy={{
                defaultValue: {
                  by: GraphQL.PaymentSearchFields.CreatedAt,
                  sortDirection: GraphQL.SortDirection.Desc,
                },
                onChange: setSortInput,
              }}
            />
          </RpiTableContainer>
        </MantineProvider>
      </div>

      {showSavedPaymentMethods && <PaymentInformationSection refetchDep={outlet === null} />}

      {showTaxExemption && <TaxExemptionSection refetchDep={outlet === null} />}

      <PaymentsFilter
        isOpened={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        filters={searchInputs}
        onApply={setSearchInputs}
      />
    </div>
  );
};
